import { isExternalUser, type PortalAccessStatus } from '../../interfaces/Users';

export const ACCESS_TENANT_ID = '6a6eee6f-75d7-4479-892d-80b5f225e004';

export const PAGE_SIZE = 10;

export type DirectoryStatus = 'active' | 'invited' | 'suspended' | 'inactive' | 'none';

export type DirectorySourceKey = 'manual' | 'entra' | 'okta' | 'scim' | 'saml' | 'oidc' | 'google' | 'external';

export interface DirectoryUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleID: string;
  roleName?: string;
  isadmin?: boolean;
  isActive?: boolean;
  isImported?: boolean;
  isExternal?: boolean;
  externalProvider?: string | null;
  portalAccessStatus?: PortalAccessStatus;
  lastLoginAt?: string | null;
  loginType?: string;
}

export function mapDirectoryUser(raw: Record<string, unknown> | DirectoryUser): DirectoryUser {
  const row = raw as Record<string, unknown>;
  const portalRaw = String(row.portalAccessStatus ?? row.portalaccessstatus ?? '').toLowerCase();
  const portalAccessStatus = (['none', 'invited', 'active', 'suspended'] as PortalAccessStatus[]).includes(
    portalRaw as PortalAccessStatus
  )
    ? (portalRaw as PortalAccessStatus)
    : undefined;

  return {
    id: String(row.id ?? row.userId ?? row.userID ?? ''),
    email: String(row.email ?? ''),
    firstName: String(row.firstName ?? row.firstname ?? ''),
    lastName: String(row.lastName ?? row.lastname ?? ''),
    tenantId: String(row.tenantId ?? row.tenantid ?? ACCESS_TENANT_ID),
    roleID: String(row.roleID ?? row.roleId ?? ''),
    roleName: row.roleName != null ? String(row.roleName) : undefined,
    isadmin: !!(row.isadmin ?? row.isAdmin),
    isActive: (row.isActive ?? row.isactive) !== false,
    isImported: !!(row.isImported ?? row.isimported),
    isExternal: !!(row.isExternal ?? row.isexternal),
    externalProvider: (row.externalProvider ?? row.externalprovider ?? null) as string | null,
    portalAccessStatus,
    lastLoginAt: (row.lastLoginAt ?? row.lastloginat ?? null) as string | null,
    loginType: row.loginType != null ? String(row.loginType) : row.logintype != null ? String(row.logintype) : undefined,
  };
}

export function displayName(user: Pick<DirectoryUser, 'firstName' | 'lastName' | 'email'>): string {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'Unknown user';
}

export function userInitials(user: Pick<DirectoryUser, 'firstName' | 'lastName' | 'email'>): string {
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return (user.email || '?').slice(0, 2).toUpperCase();
}

export function isWorkspaceAdmin(user: DirectoryUser): boolean {
  if (user.isadmin) return true;
  return (user.roleName || '').trim().toLowerCase() === 'admin';
}

export function userStatus(user: DirectoryUser): DirectoryStatus {
  const portal = (user.portalAccessStatus || '').toLowerCase();
  if (portal === 'active' || portal === 'invited' || portal === 'suspended' || portal === 'none') {
    return portal;
  }
  return user.isActive === false ? 'inactive' : 'active';
}

export function statusLabel(status: DirectoryStatus): string {
  if (status === 'active') return 'Active';
  if (status === 'invited') return 'Invited';
  if (status === 'suspended') return 'Suspended';
  if (status === 'inactive') return 'Inactive';
  return 'None';
}

export function userSourceKey(user: DirectoryUser): DirectorySourceKey {
  if (!isExternalUser(user)) return 'manual';
  const raw = `${user.externalProvider || ''} ${user.loginType || ''}`.toLowerCase();
  if (raw.includes('okta')) return 'okta';
  if (raw.includes('scim')) return 'scim';
  if (raw.includes('google')) return 'google';
  if (raw.includes('saml')) return 'saml';
  if (raw.includes('oidc')) return 'oidc';
  if (raw.includes('entra') || raw.includes('azure') || raw.includes('microsoft')) return 'entra';
  return 'entra';
}

export function sourceLabel(key: DirectorySourceKey): string {
  switch (key) {
    case 'manual':
      return 'Manual';
    case 'entra':
      return 'Microsoft Entra ID';
    case 'okta':
      return 'Okta';
    case 'scim':
      return 'SCIM';
    case 'saml':
      return 'SAML';
    case 'oidc':
      return 'OIDC';
    case 'google':
      return 'Google';
    default:
      return 'External';
  }
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return date.toLocaleString();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

export function visiblePageNumbers(current: number, total: number, windowSize = 5): number[] {
  if (total <= windowSize) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  const end = Math.min(total, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
